import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function JobMaterials({ value, original, onChange }) {
  const [mats, setMats] = useState([]);
  const [sel, setSel] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get("/materials").then((r) => setMats(r.data));
  }, []);

  const orig = (id) => (original || []).filter((x) => x.material_id === id).reduce((a, x) => a + x.quantity, 0);
  const used = (id) => value.filter((x) => x.material_id === id).reduce((a, x) => a + x.quantity, 0);
  const available = (m) => +(m.quantity + orig(m.id) - used(m.id)).toFixed(3);

  const add = () => {
    const m = mats.find((x) => x.id === sel);
    const q = Number(qty);
    if (!m || !(q > 0)) return;
    onChange([...value, { material_id: m.id, name: m.name, unit: m.unit, quantity: q }]);
    setSel("");
    setQty(1);
  };

  const selected = mats.find((x) => x.id === sel);
  const tooMuch = selected && Number(qty) > available(selected);

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="job-materials">
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Material utilizado (se descuenta del stock)</p>
      {value.length === 0 && <p className="text-xs text-stone-400">Sin materiales asignados</p>}
      {value.map((x, i) => (
        <div key={i} className="flex items-center justify-between gap-2 text-sm bg-white rounded-md border px-3 py-1.5" data-testid={`job-material-row-${i}`}>
          <span className="font-medium">{x.name}</span>
          <div className="flex items-center gap-2">
            <span className="font-mono">{x.quantity} {x.unit}</span>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange(value.filter((_, idx) => idx !== i))} data-testid={`job-material-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="flex-1 bg-white" data-testid="job-material-select"><SelectValue placeholder="Elegir material" /></SelectTrigger>
          <SelectContent>
            {mats.length === 0 && <div className="px-3 py-2 text-sm text-stone-400">No hay materiales en stock</div>}
            {mats.map((m) => (
              <SelectItem key={m.id} value={m.id} disabled={available(m) <= 0}>
                {m.name} — disp. {available(m)} {m.unit}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="number" step="any" min="0" className="w-24 bg-white" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="job-material-qty-input" />
        <Button type="button" variant="outline" onClick={add} disabled={!selected || tooMuch || !(Number(qty) > 0)} data-testid="job-material-add-button">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {tooMuch && <p className="text-xs text-red-600" data-testid="job-material-error">Solo hay {available(selected)} {selected.unit} disponibles</p>}
    </div>
  );
}
